import { __awaiter, __rest } from "tslib";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { yupResolver } from '@hookform/resolvers/yup';
import { CaretDownIcon, GiftIcon } from '@yonderland/andes-icons';
import { Box, ButtonAlpha, CollapsibleContent, CollapsibleToggle, Grid, Icon, SelectionCard, StackBlock, StackInline, TextAlpha, TextField, useCollapsible, useMediaQueryMatchesOnDesktop, } from '@yonderland/andes-react';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { I18N_NAMESPACES } from '../../../constants/i18nConstants';
import { ScrollUtil } from '../../../util/ScrollUtil';
import { FormField } from '../../form/FormField';
import { SuccessMessageBanner } from './SuccessMessageBanner';
const BASKET_COUPON_CODE_ID = 'basket-coupon-code';
const BASKET_COUPON_WEBCODE_ID = 'basket-coupon-webcode';
export const VoucherBlock = ({ submitButtonLabel, onSubmit, isPaymentPage, isOpenByDefault = false, validateRegex, onAddSuccess, onAddError, onFieldsError, }) => {
    const { iconClasses, toggleProps, handleToggle, isOpen } = useCollapsible({ open: isOpenByDefault });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [successMessages, setSuccessMessages] = useState([]);
    const [showWebcode, setShowWebcode] = useState(false);
    const isDesktop = useMediaQueryMatchesOnDesktop();
    const { t } = useTranslation([I18N_NAMESPACES.checkout, I18N_NAMESPACES.buy]);
    const validationSchema = Yup.object({
        code: Yup.string().required(t(`${I18N_NAMESPACES.checkout}:checkout.basket.voucher.barcode.errormessage`)),
        webcode: showWebcode
            ? Yup.string().required(t(`${I18N_NAMESPACES.checkout}:checkout.basket.voucher.webcode.errormessage`))
            : Yup.string(),
    });
    const formProps = useForm({
        defaultValues: { code: '', webcode: '' },
        resolver: yupResolver(validationSchema),
    });
    const onCodeChange = (code) => {
        validateRegex && setShowWebcode(new RegExp(validateRegex).test(code));
    };
    const handleSubmit = (_a) => __awaiter(void 0, [_a], void 0, function* ({ code, webcode }) {
        var _b, _c;
        if ((showWebcode && isPaymentPage) || !isPaymentPage) {
            try {
                setIsSubmitting(true);
                const appliedVoucher = yield onSubmit(code, webcode);
                setSuccessMessages(previousSuccessMessages => [
                    ...previousSuccessMessages,
                    { amount: appliedVoucher === null || appliedVoucher === void 0 ? void 0 : appliedVoucher.amountUsedForThisBasket },
                ]);
                formProps.reset();
                onAddSuccess(code);
                setIsSubmitting(false);
                isOpen && handleToggle();
            }
            catch (error) {
                setIsSubmitting(false);
                const message = (_c = (_b = error.response) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.message;
                if (message) {
                    if (showWebcode) {
                        formProps.setError('webcode', { message });
                        ScrollUtil.scrollToErrorElement(document.getElementById(BASKET_COUPON_WEBCODE_ID), isDesktop);
                    }
                    else {
                        formProps.setError('code', { message });
                        ScrollUtil.scrollToErrorElement(document.getElementById(BASKET_COUPON_CODE_ID), isDesktop);
                    }
                }
                onAddError(message);
            }
        }
        if (!showWebcode && isPaymentPage) {
            formProps.setError('code', {
                type: 'custom',
                message: t(`${I18N_NAMESPACES.checkout}:checkout.basket.voucher.mediacode.error`),
            });
            ScrollUtil.scrollToErrorElement(document.getElementById(BASKET_COUPON_CODE_ID), isDesktop);
        }
    });
    const handleSuccessMessageDismiss = (index) => () => {
        setSuccessMessages(prev => prev.filter((_successMessage, scmIndex) => scmIndex !== index));
    };
    useEffect(() => {
        if (onFieldsError && Object.keys(formProps.formState.errors).length) {
            const errors = Object.entries(formProps.formState.errors).reduce((res, [key, { message }]) => {
                if (message)
                    res[key] = message;
                return res;
            }, {});
            onFieldsError(errors);
        }
    }, [formProps.formState.errors.code, formProps.formState.errors.webcode]);
    return (_jsx(SelectionCard, { onClick: handleToggle, testId: "voucher_block", children: _jsxs(SelectionCard.Cell, { section: "content", children: [_jsxs(CollapsibleToggle, Object.assign({}, toggleProps, { onClick: e => {
                        e.stopPropagation();
                        handleToggle();
                    }, tag: "div", testId: "voucher_toggle", children: [_jsxs(StackInline, { wrap: "nowrap", contentAlign: "between", gap: "2", children: [_jsxs(StackInline, { wrap: "nowrap", gap: "2", children: [_jsx(Icon, { source: GiftIcon, color: "neutral", size: "sm" }), _jsx(TextAlpha, { children: t(`${I18N_NAMESPACES.buy}:buy.payment.voucher.title`) })] }), _jsx(StackInline, { itemsAlign: "center", children: _jsx("div", { className: iconClasses, children: _jsx(Icon, { source: CaretDownIcon }) }) })] }), (successMessages === null || successMessages === void 0 ? void 0 : successMessages.length) > 0 && (_jsx(Box, { paddingBlockStart: "3", children: _jsx(StackBlock, { gap: "2", children: successMessages.map((successMessage, index) => (_jsx(SuccessMessageBanner, { amount: successMessage.amount, onDismiss: handleSuccessMessageDismiss(index) }, index))) }) }))] })), _jsx(CollapsibleContent, { isOpen: isOpen, children: _jsx(Box, { padding: "2", children: _jsx(FormProvider, Object.assign({}, formProps, { children: _jsx("form", { onSubmit: formProps.handleSubmit(handleSubmit), name: "voucher-form", children: _jsx(Grid, { columns: 1, gapX: 4, gapY: 6, children: _jsx(Grid.Cell, { children: _jsxs(StackBlock, { gap: "3", children: [_jsx(FormField, { name: "code", required: true, children: (_a) => {
                                                        var _b = _a.fieldProps, { ref } = _b, fieldProps = __rest(_b, ["ref"]), { error } = _a;
                                                        return (_jsx(TextField, Object.assign({}, fieldProps, { innerRef: ref, label: t('checkout:checkout.basket.voucher.barcode.label'), name: fieldProps.name, id: BASKET_COUPON_CODE_ID, testId: "voucher_code", error: error, maxLength: 50, onKeyUp: () => {
                                                                onCodeChange(fieldProps.value);
                                                            }, onClick: event => event.stopPropagation() })));
                                                    } }), showWebcode && (_jsx(FormField, { name: "webcode", children: (_a) => {
                                                        var _b = _a.fieldProps, { ref } = _b, fieldProps = __rest(_b, ["ref"]), { error } = _a;
                                                        return (_jsx(TextField, Object.assign({}, fieldProps, { innerRef: ref, label: t('checkout:checkout.basket.voucher.webcode.label'), name: fieldProps.name, id: BASKET_COUPON_WEBCODE_ID, testId: "voucher_webcode", error: error, maxLength: 50, onClick: event => event.stopPropagation() })));
                                                    } })), _jsx(ButtonAlpha, { type: "submit", theme: "neutral", variant: "secondary", text: submitButtonLabel, onClick: event => {
                                                        event.stopPropagation();
                                                    }, state: isSubmitting ? 'loading' : 'interactive', testId: "voucher_add" })] }) }) }) }) })) }) })] }) }));
};
